import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQC } from './QCContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Switch } from '../../components/ui/switch';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../../components/ui/dialog';
import { toast } from 'sonner';
import {
  ArrowLeft,
  Settings,
  Shield,
  AlertTriangle,
  Clock,
  Users,
  Plus,
  Trash2,
  Zap,
  CheckCircle2,
} from 'lucide-react';

export const QCAdminSettings = () => {
  const navigate = useNavigate();
  const { api, user } = useQC();
  const [loading, setLoading] = useState(true);
  
  // Config state
  const [ackConfig, setAckConfig] = useState({
    ack_deadline_minutes: 30,
    escalation_enabled: true,
    escalation_levels: [],
    auto_reassign_enabled: true,
    max_declines_per_window: 1,
  });
  
  // Emergency settings
  const [emergencySettings, setEmergencySettings] = useState({
    response_timeout_minutes: 10,
    normal_timeout_minutes: 30,
    priority_bypass: true,
    auto_assign_on_timeout: true,
  });
  
  // Dialogs
  const [editTimeoutOpen, setEditTimeoutOpen] = useState(false);
  const [editEscalationOpen, setEditEscalationOpen] = useState(false);
  const [newTimeout, setNewTimeout] = useState(30);
  const [newEscalationLevels, setNewEscalationLevels] = useState([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      const response = await api.get('/admin/canvass/ack-config');
      if (response.data) {
        setAckConfig(response.data);
        setNewTimeout(response.data.ack_deadline_minutes);
        setNewEscalationLevels(response.data.escalation_levels || []);
      }
    } catch (error) {
      console.error('Error fetching settings:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateTimeout = async () => {
    setSaving(true);
    try {
      await api.put('/admin/canvass/ack-config/timeout', { minutes: newTimeout });
      toast.success('Timeout updated');
      setEditTimeoutOpen(false);
      fetchSettings();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update timeout');
    } finally {
      setSaving(false);
    }
  };

  const handleUpdateEscalationLevels = async () => {
    setSaving(true);
    try {
      await api.put('/admin/canvass/ack-config/escalation-levels', { levels: newEscalationLevels });
      toast.success('Escalation levels updated');
      setEditEscalationOpen(false);
      fetchSettings();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update escalation levels');
    } finally {
      setSaving(false);
    }
  };

  const handleToggleAutoReassign = async (enabled) => {
    try {
      await api.put(`/admin/canvass/ack-config/auto-reassign?enabled=${enabled}`);
      toast.success(`Auto-reassign ${enabled ? 'enabled' : 'disabled'}`);
      fetchSettings();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to toggle auto-reassign');
    }
  };

  const addEscalationLevel = () => {
    setNewEscalationLevels([...newEscalationLevels, { afterMinutes: 10, notifyRole: 'SUPERVISOR' }]);
  };

  const removeEscalationLevel = (index) => {
    setNewEscalationLevels(newEscalationLevels.filter((_, i) => i !== index));
  };

  const updateEscalationLevel = (index, field, value) => {
    const updated = [...newEscalationLevels];
    updated[index] = { ...updated[index], [field]: field === 'afterMinutes' ? parseInt(value) : value };
    setNewEscalationLevels(updated);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="qc-admin-settings">
      <Button variant="ghost" onClick={() => navigate('/quickcanvass')} className="gap-2">
        <ArrowLeft className="h-4 w-4" />
        Back to Dashboard
      </Button>

      <div>
        <h1 className="text-4xl md:text-5xl font-black tracking-tight">
          Admin Settings
        </h1>
        <p className="text-muted-foreground mt-2">
          Configure canvassing behavior and rules
        </p>
      </div>

      {/* Acknowledgement Settings */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Clock className="h-5 w-5" />
            Acknowledgement Settings
          </CardTitle>
          <CardDescription>Configure timeouts and auto-reassignment</CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {/* Timeout */}
            <div className="p-4 border rounded-lg">
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-2">
                  <Clock className="h-4 w-4 text-muted-foreground" />
                  <span className="font-medium">Timeout</span>
                </div>
                <Button variant="outline" size="sm" onClick={() => setEditTimeoutOpen(true)}>
                  Edit
                </Button>
              </div>
              <p className="text-2xl font-bold">{ackConfig.ack_deadline_minutes} min</p>
              <p className="text-sm text-muted-foreground">Time to acknowledge</p>
            </div>

            {/* Auto Reassign */}
            <div className="p-4 border rounded-lg">
              <div className="flex items-center justify-between mb-2">
                <span className="font-medium">Auto-Reassign</span>
                <Switch 
                  checked={ackConfig.auto_reassign_enabled}
                  onCheckedChange={handleToggleAutoReassign}
                />
              </div>
              <p className="text-sm text-muted-foreground">
                {ackConfig.auto_reassign_enabled 
                  ? 'Auto-reassign on decline/expire' 
                  : 'Manual reassignment only'}
              </p>
            </div>

            {/* Escalation Levels */}
            <div className="p-4 border rounded-lg">
              <div className="flex items-center justify-between mb-2">
                <span className="font-medium">Escalation</span>
                <Button variant="outline" size="sm" onClick={() => setEditEscalationOpen(true)}>
                  Edit
                </Button>
              </div>
              <p className="text-2xl font-bold">{ackConfig.escalation_levels?.length || 0}</p>
              <p className="text-sm text-muted-foreground">Escalation levels</p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Escalation Rules Display */}
      {ackConfig.escalation_levels?.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <AlertTriangle className="h-5 w-5" />
              Escalation Rules
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              {ackConfig.escalation_levels.map((level, idx) => (
                <div key={idx} className="flex items-center justify-between p-3 border rounded-lg">
                  <div className="flex items-center gap-3">
                    <Badge variant="outline">Level {idx + 1}</Badge>
                    <span>After <strong>{level.afterMinutes}</strong> minutes</span>
                  </div>
                  <Badge>{level.notifyRole}</Badge>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Emergency Mode Settings */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Zap className="h-5 w-5 text-red-500" />
            Emergency Mode Settings
          </CardTitle>
          <CardDescription>Configure behavior during emergency situations</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="p-4 border rounded-lg">
              <Label>Emergency Response Timeout</Label>
              <p className="text-2xl font-bold mt-2">{emergencySettings.response_timeout_minutes} min</p>
              <p className="text-sm text-muted-foreground">Faster response required</p>
            </div>
            <div className="p-4 border rounded-lg">
              <Label>Normal Response Timeout</Label>
              <p className="text-2xl font-bold mt-2">{emergencySettings.normal_timeout_minutes} min</p>
              <p className="text-sm text-muted-foreground">Standard response time</p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Edit Timeout Dialog */}
      <Dialog open={editTimeoutOpen} onOpenChange={setEditTimeoutOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Edit Acknowledgement Timeout</DialogTitle>
          </DialogHeader>
          <div className="py-4">
            <Label>Timeout (minutes)</Label>
            <Input
              type="number"
              min={5}
              max={1440}
              value={newTimeout}
              onChange={(e) => setNewTimeout(parseInt(e.target.value) || 30)}
              className="mt-2"
            />
            <p className="text-sm text-muted-foreground mt-2">
              Min: 5 minutes, Max: 24 hours (1440 minutes)
            </p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditTimeoutOpen(false)}>Cancel</Button>
            <Button onClick={handleUpdateTimeout} disabled={saving}>
              {saving ? 'Saving...' : 'Save'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Escalation Dialog */}
      <Dialog open={editEscalationOpen} onOpenChange={setEditEscalationOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Edit Escalation Levels</DialogTitle>
          </DialogHeader>
          <div className="py-4 space-y-4">
            {newEscalationLevels.map((level, index) => (
              <div key={index} className="flex items-center gap-3 p-3 border rounded-lg">
                <div className="flex-1">
                  <Label className="text-xs">After (minutes)</Label>
                  <Input
                    type="number"
                    min={1}
                    value={level.afterMinutes}
                    onChange={(e) => updateEscalationLevel(index, 'afterMinutes', e.target.value)}
                    className="mt-1"
                  />
                </div>
                <div className="flex-1">
                  <Label className="text-xs">Notify Role</Label>
                  <Select 
                    value={level.notifyRole} 
                    onValueChange={(v) => updateEscalationLevel(index, 'notifyRole', v)}
                  >
                    <SelectTrigger className="mt-1">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="SUPERVISOR">Supervisor</SelectItem>
                      <SelectItem value="ADMIN">Admin</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <Button 
                  variant="ghost" 
                  size="sm" 
                  className="text-red-500" 
                  onClick={() => removeEscalationLevel(index)}
                >
                  <Trash2 className="h-4 w-4" />
                </Button>
              </div>
            ))}
            <Button variant="outline" onClick={addEscalationLevel} className="w-full">
              <Plus className="h-4 w-4 mr-2" /> Add Level
            </Button>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditEscalationOpen(false)}>Cancel</Button>
            <Button onClick={handleUpdateEscalationLevels} disabled={saving}>
              {saving ? 'Saving...' : 'Save'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default QCAdminSettings;
